import React, { useEffect, useRef, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import type {
  DieselTankKind,
  ProposalSection,
  PropaneTankStatus,
  SurveyFuel,
  SystemChange,
} from '@officehub/shared';
import {
  DIESEL_TANK_KINDS,
  MAX_PROPOSAL_TEXT_LENGTH,
  PROPANE_TANK_STATUSES,
  SURVEY_FUELS,
  SYSTEM_CHANGES,
  effectiveProposedFuel,
  proposalFieldsForFuel,
} from './proposalRules';
import { INPUT_CLASS, LABEL_CLASS, TOGGLE_ACTIVE, TOGGLE_INACTIVE } from './styles';

export interface ProposalFormProps {
  data: ProposalSection;
  /**
   * Save a patch. May return a promise: a rejection means the server refused or the save failed, and the
   * form then drops the answers it was holding (so the screen goes back to what is really stored) and says
   * "Not saved". A void return is treated as "handed over, outcome unknown".
   */
  onUpdate: (d: Partial<ProposalSection>) => void | Promise<void>;
  /**
   * When typed text is handed to `onUpdate`. 'blur' (default) suits a parent whose onUpdate saves straight
   * away (one save per box, not per keystroke). 'change' suits a parent that only holds the value until a
   * Save button, so a tap on Save can never miss a half-finished box.
   */
  commitOn?: 'blur' | 'change';
}

const TOGGLE_BASE = 'flex-1 py-2.5 text-xs rounded-xl transition-all duration-150 min-h-[48px]';

const FUEL_LABELS: Record<SurveyFuel, string> = { natural_gas: 'Natural gas', propane: 'Propane', diesel: 'Diesel' };
const CHANGE_LABELS: Record<SystemChange, string> = { keep: 'Keep as is', replace: 'Replace', new: 'New install' };
const PROPANE_STATUS_LABELS: Record<PropaneTankStatus, string> = { existing: 'Existing tank', new: 'New tank' };
const DIESEL_KIND_LABELS: Record<DieselTankKind, string> = {
  sub_base: 'Sub-base (unit on top)',
  above_ground: 'Separate above-ground',
  underground: 'Underground',
};

type ChoiceField =
  | 'proposedFuel'
  | 'fuelSupplyChange'
  | 'propaneTankStatus'
  | 'dieselTankKind'
  | 'panelChange'
  | 'transferSwitchChange'
  | 'padChange';

type TextField =
  | 'propaneTankLocation'
  | 'dieselTankLocation'
  | 'panelChangeNotes'
  | 'transferSwitchExisting'
  | 'transferSwitchProposed';

/** An answer the person just gave that the parent's data has not caught up with. `base` is the stored value it was given over. */
interface Held<T> {
  value: T;
  base: string | null;
}

/** Text handed to onUpdate, and the stored value it was handed over. Stale the moment the stored value moves. */
interface Sent {
  value: string | null;
  base: string | null;
}

const clean = (raw: string): string | null => (raw.trim() === '' ? null : raw.trim());

/**
 * What we propose, and what changes versus what stays. Every answer starts blank
 * ("not asked yet"), never pre-selected; tapping the chosen answer again clears it
 * (emits null, which survey_updateSection deletes). Only the questions for the
 * CURRENT fuel are shown: answers left from another fuel stay stored but are never
 * shown or read.
 *
 * The parent's data only updates after the save round-trip, so what the person just did is
 * held here and shown until the stored value moves off the one it was given over. A held answer is
 * dropped the moment the stored value moves (a landed save, another device, the AI), so it can never
 * resurface later, and dropped when its save is refused, so the screen never shows an unsaved answer as
 * saved. Text is saved once on blur (or per change with commitOn="change"); text still waiting when a
 * choice is tapped or the form goes away is handed over then, because tapping a button on a phone does
 * not blur the box. Everything handed over in one tap or one flush goes in ONE patch.
 */
export function ProposalForm({ data, onUpdate, commitOn = 'blur' }: ProposalFormProps): React.ReactElement {
  const [drafts, setDrafts] = useState<Partial<Record<TextField, Held<string>>>>({});
  const [chosen, setChosen] = useState<Partial<Record<ChoiceField, Held<string | null>>>>({});
  const [saveFailed, setSaveFailed] = useState(false);
  // The fuel was just switched and the fuel supply answer cleared with it: ask it again until it is answered.
  const [reanswerSupply, setReanswerSupply] = useState(false);
  // What has already been handed to onUpdate per text field, so a later flush never sends it twice.
  const sent = useRef<Partial<Record<TextField, Sent>>>({});

  const storedIn = (src: ProposalSection, field: ChoiceField | TextField): string | null =>
    (src[field] as string | undefined) ?? null;
  const stored = (field: ChoiceField | TextField): string | null => storedIn(data, field);

  const shownChoice = (field: ChoiceField): string | null => {
    const held = chosen[field];
    return held && held.base === stored(field) ? held.value : stored(field);
  };
  const shownText = (field: TextField): string => {
    const held = drafts[field];
    return held && held.base === stored(field) ? held.value : (stored(field) ?? '');
  };

  const fuel = shownChoice('proposedFuel');
  const questions = proposalFieldsForFuel(effectiveProposedFuel(fuel));

  // The stored value moved: whatever was held over the old one is out of date, and so is any "already sent".
  useEffect(() => {
    const prune = <T extends Held<unknown>>(prev: Partial<Record<string, T>>): Partial<Record<string, T>> => {
      const kept = Object.entries(prev).filter(([field, held]) => held && held.base === stored(field as ChoiceField));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    };
    setChosen((prev) => prune(prev) as typeof prev);
    setDrafts((prev) => prune(prev) as typeof prev);
    for (const field of Object.keys(sent.current) as TextField[]) {
      if (sent.current[field]?.base !== stored(field)) delete sent.current[field];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `stored` is a closure over `data`
  }, [data]);

  /**
   * Forget a refused patch's answers — but ONLY the ones still holding the exact value that was
   * just refused. A newer tap made while this save was failing (another choice, more typing) has
   * already replaced the held entry with a different value, and must not be wiped by an older
   * save's rejection landing late.
   */
  const forget = (patch: Record<string, unknown>) => {
    const drop = <T extends { value: unknown }>(prev: Partial<Record<string, T>>): Partial<Record<string, T>> => {
      const next = { ...prev };
      for (const [f, v] of Object.entries(patch)) {
        if (next[f]?.value === v) delete next[f];
      }
      return next;
    };
    setChosen((prev) => drop(prev) as typeof prev);
    setDrafts((prev) => {
      const next = { ...prev };
      for (const [f, v] of Object.entries(patch)) {
        const held = next[f as TextField];
        if (held && clean(held.value) === v) delete next[f as TextField];
      }
      return next;
    });
    for (const [f, v] of Object.entries(patch)) {
      const was = sent.current[f as TextField];
      if (was && was.value === v) delete sent.current[f as TextField];
    }
  };

  // A null clears the stored field; the section type has no null, so the one widening cast lives here.
  // `quiet` is for the unmount flush: the form is gone, so it has no state to fix and no message to show.
  const emit = (patch: Record<string, unknown>, quiet = false) => {
    if (!quiet) setSaveFailed(false);
    let result: void | Promise<void>;
    try {
      result = onUpdate(patch as Partial<ProposalSection>);
    } catch (e) {
      result = Promise.reject(e);
    }
    Promise.resolve(result).catch(() => {
      if (quiet) return;
      forget(patch);
      // Only a refused patch that carried the clear takes the re-answer notice with it.
      if ('fuelSupplyChange' in patch) setReanswerSupply(false);
      setSaveFailed(true);
    });
  };

  const alreadySent = (src: ProposalSection, field: TextField, next: string | null): boolean => {
    const was = sent.current[field];
    return !!was && was.value === next && was.base === storedIn(src, field);
  };

  /** The text boxes whose text has not been handed over yet, as one patch (blank clears with null). */
  const pendingText = (src: { drafts: typeof drafts; data: ProposalSection }): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    for (const [field, held] of Object.entries(src.drafts) as [TextField, Held<string>][]) {
      const now = storedIn(src.data, field);
      if (held.base !== now) continue; // the stored value moved on: this draft is out of date
      const next = clean(held.value);
      if (next !== now && !alreadySent(src.data, field, next)) patch[field] = next;
    }
    return patch;
  };

  const markSent = (patch: Record<string, unknown>) => {
    for (const [field, value] of Object.entries(patch)) {
      if (field in drafts) sent.current[field as TextField] = { value: value as string | null, base: stored(field as TextField) };
    }
  };

  // Leaving the form with a box still focused fires no blur: hand over what is still waiting, once.
  const latest = useRef({ drafts, data, emit, pendingText });
  latest.current = { drafts, data, emit, pendingText };
  useEffect(
    () => () => {
      const { drafts: d, data: stored2, emit: send, pendingText: pending } = latest.current;
      const patch = pending({ drafts: d, data: stored2 });
      if (Object.keys(patch).length > 0) send(patch, true);
    },
    [],
  );

  const choose = (field: ChoiceField, value: string) => {
    const next = shownChoice(field) === value ? null : value;
    // The fuel supply answer (keep / replace / new) is about ONE fuel's supply: the gas service, the propane
    // tank or the diesel tank. When the fuel the questions are for changes, it is cleared in the same patch
    // and asked again, rather than left looking answered about something else.
    const switching =
      field === 'proposedFuel' &&
      effectiveProposedFuel(shownChoice('proposedFuel')) !== effectiveProposedFuel(next) &&
      shownChoice('fuelSupplyChange') !== null;
    // Text typed in a box this tap may hide is saved first, in the same patch as the tap.
    const patch = { ...pendingText({ drafts, data }), [field]: next, ...(switching ? { fuelSupplyChange: null } : {}) };
    markSent(patch);
    setChosen((prev) => ({
      ...prev,
      [field]: { value: next, base: stored(field) },
      ...(switching ? { fuelSupplyChange: { value: null, base: stored('fuelSupplyChange') } } : {}),
    }));
    if (switching) setReanswerSupply(true);
    if (field === 'fuelSupplyChange') setReanswerSupply(false);
    emit(patch);
  };

  const typeInto = (field: TextField, raw: string) => {
    setDrafts((prev) => ({ ...prev, [field]: { value: raw, base: stored(field) } }));
    if (commitOn !== 'change') return;
    const next = clean(raw);
    sent.current[field] = { value: next, base: stored(field) };
    if (next !== stored(field)) emit({ [field]: next });
  };

  const commitText = (field: TextField) => {
    const held = drafts[field];
    if (!held || held.base !== stored(field)) return;
    const next = clean(held.value);
    if (next === stored(field)) {
      setDrafts((prev) => {
        const { [field]: _done, ...rest } = prev;
        return rest;
      });
      return;
    }
    if (alreadySent(data, field, next)) return;
    sent.current[field] = { value: next, base: stored(field) };
    emit({ [field]: next });
  };

  const choiceGroup = <V extends string>(
    field: ChoiceField,
    label: string,
    values: readonly V[],
    labels: Record<V, string>,
  ) => (
    <div key={field}>
      <div id={`proposal-${field}-label`} className={LABEL_CLASS}>
        {label}
      </div>
      <div role="group" aria-labelledby={`proposal-${field}-label`} className="flex gap-2">
        {values.map((value) => {
          const active = shownChoice(field) === value;
          return (
            <button
              key={value}
              type="button"
              data-testid={`proposal-${field}-${value}`}
              aria-pressed={active}
              onClick={() => choose(field, value)}
              className={`${TOGGLE_BASE} ${active ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}`}
            >
              {labels[value]}
            </button>
          );
        })}
      </div>
    </div>
  );

  const textBox = (field: TextField, label: string, placeholder: string) => (
    <div key={field}>
      <label className={LABEL_CLASS} htmlFor={`proposal-${field}`}>
        {label}
      </label>
      <input
        id={`proposal-${field}`}
        data-testid={`field-${field}`}
        type="text"
        maxLength={MAX_PROPOSAL_TEXT_LENGTH}
        className={INPUT_CLASS}
        value={shownText(field)}
        onChange={(e) => typeInto(field, e.target.value)}
        onBlur={() => commitText(field)}
        placeholder={placeholder}
      />
    </div>
  );

  const heading = (title: string) => (
    <div className="flex items-center gap-2 px-1">
      <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">{title}</h3>
      <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 px-1">
        <ClipboardList className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">What we propose</h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {saveFailed && (
        <div role="alert" data-testid="proposal-save-failed" className="text-xs font-bold text-red-700 px-1">
          Not saved. The last answer did not go through; check your connection and try again.
        </div>
      )}

      {choiceGroup('proposedFuel', 'Fuel for the proposed unit', SURVEY_FUELS, FUEL_LABELS)}

      {heading('Fuel supply')}
      {reanswerSupply && shownChoice('fuelSupplyChange') === null && (
        <div data-testid="proposal-fuel-switched" className="text-xs font-bold text-amber-800 px-1">
          You changed the fuel, so the fuel supply answer was cleared. Answer it again for the new fuel.
        </div>
      )}
      {choiceGroup('fuelSupplyChange', 'Fuel supply (gas service or tank)', SYSTEM_CHANGES, CHANGE_LABELS)}
      {questions.propaneTank && (
        <>
          {choiceGroup('propaneTankStatus', 'Propane tank', PROPANE_TANK_STATUSES, PROPANE_STATUS_LABELS)}
          {textBox('propaneTankLocation', 'Where the propane tank goes', 'e.g. behind the garage, 10 ft from the fence')}
        </>
      )}
      {questions.dieselTank && (
        <>
          {choiceGroup('dieselTankKind', 'Diesel tank kind', DIESEL_TANK_KINDS, DIESEL_KIND_LABELS)}
          {textBox('dieselTankLocation', 'Where the diesel tank goes', 'e.g. side yard, under the unit')}
        </>
      )}

      {heading('Electrical panel')}
      {choiceGroup('panelChange', 'Electrical panel', SYSTEM_CHANGES, CHANGE_LABELS)}
      {textBox('panelChangeNotes', 'Panel notes', 'e.g. add a sub-panel, upgrade the main breaker')}

      {heading('Transfer switch')}
      {textBox('transferSwitchExisting', 'Existing transfer switch', 'What is there today (or none)')}
      {choiceGroup('transferSwitchChange', 'Transfer switch', SYSTEM_CHANGES, CHANGE_LABELS)}
      {textBox('transferSwitchProposed', 'Proposed transfer switch', 'What we propose')}

      {heading('Generator pad')}
      {choiceGroup('padChange', 'Generator pad', SYSTEM_CHANGES, CHANGE_LABELS)}
    </div>
  );
}
